/***
 *
 *   CLIENT ONBOARDING V2 — PICKERS, DRAFT AND COMPLETION GATE
 *   The public v2 view's logic, kept out of React so it runs against a real
 *   survey-core Model in client-onboarding-v2-pickers.test.js.
 *
 *   THE PICKER SYNC IS THE RISKY PART (lifted from
 *   prototypes/client-onboarding-v2/app.js). The three party pickers
 *   (rules.PICKER_QUESTIONS: holder_ref, primary_entity_id,
 *   ceo_equivalent_person_id) have no choices in the schema, because
 *   choicesFromQuestion cannot read a paneldynamic. Their choices must land on
 *   the TEMPLATE question and on EVERY panel that already exists, at both
 *   nesting levels: a panel keeps the choice list it was created with. And
 *   assigning choices does not drop a value that is no longer offered, so a pick
 *   of a deleted party would pass required-field validation and point at
 *   nothing. The sync clears such a pick, and the view tells the client (R9).
 *
 *   Party ids are minted here, once per panel: p-<token> for a person and
 *   e-<token> for an entity, the only ids the public endpoint accepts. An
 *   expression would re-fire when a sibling panel is removed and renumber ids
 *   that role rows already point at.
 *
 **********/

// The shared rules are CommonJS, because the server requires them too.
import rules from "./client-onboarding-v2-rules.js";

// The id forms the public endpoint accepts (PERSON_ID_PATTERN and
// ENTITY_ID_PATTERN in controller/clientOnboardingV2Controller.js). Built from
// a string: the Tailwind watcher reads a literal p-plus-bracket regex as an
// arbitrary padding class and writes a broken rule into output.css.
const ID_TAIL = "-[A-Za-z0-9_-]{1,64}$";
const ID_PATTERNS = {
  p: new RegExp(`^p${ID_TAIL}`),
  e: new RegExp(`^e${ID_TAIL}`),
};

// The message the public endpoint answers a rules refusal with
// (RULES_REFUSED_MESSAGE in controller/clientOnboardingV2Controller.js), so the
// browser gate and the server 400 read the same.
export const RULES_REFUSED_MESSAGE =
  "Some answers need changes before the form can be submitted.";

// Every 403 lands here: the ops flag turned off, or the firm disabled the
// integration. The draft never holds a TFN (R16), so the message says so.
export const LINK_CLOSED_MESSAGE =
  "This onboarding link was closed while you were filling it in. Your answers, except any tax file number, are saved in this browser. Please contact the firm that sent it to you.";

export const SUBMIT_FAILED_MESSAGE =
  "The form could not be submitted. Check your connection and try again.";

const FORM_GONE_MESSAGE =
  "This onboarding form is no longer available. Please contact the firm that sent it to you.";

const { isPlainObject } = rules;

// The models whose sync is running. Minting an id or clearing a pick sets a
// value, which fires onValueChanged again; the sync already reads the values it
// sets, so the nested event has nothing to add.
const syncing = new WeakSet();

// The last label each party id had, for each model, so a cleared pick can name
// the party that went. The picker's own old choices cannot: survey-core copies
// a template question's new choices onto every row before the row is synced.
const lastLabels = new WeakMap();

// --- party ids -------------------------------------------------------------

function randomToken() {
  const cryptoApi = typeof window !== "undefined" ? window.crypto : undefined;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function") {
    return cryptoApi.randomUUID().replace(/-/g, "");
  }
  if (cryptoApi && typeof cryptoApi.getRandomValues === "function") {
    return Array.from(cryptoApi.getRandomValues(new Uint8Array(16)), (byte) =>
      byte.toString(16).padStart(2, "0")
    ).join("");
  }
  // Ids only need to be unique inside one submission, and ensurePartyIds
  // re-mints a duplicate.
  return Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
}

export function newPartyId(prefix) {
  return `${prefix}-${randomToken()}`;
}

/*
 * ensurePartyIds()
 * Gives every person and entity panel an id, once. An id that is already valid
 * and unused is never changed. A missing id, one the endpoint would refuse, or a
 * copy of an earlier panel's id (a firm's copy can carry the last panel's values
 * into a new one) gets a new id, because the client cannot see or fix it.
 */
export function ensurePartyIds(model) {
  const seen = new Set();
  const visit = (listName, idName, prefix) => {
    const list = model.getQuestionByName(listName);
    (list?.panels || []).forEach((panel) => {
      const question = panel.getQuestionByName(idName);
      if (!question) return;
      const id = question.value;
      if (typeof id === "string" && ID_PATTERNS[prefix].test(id) && !seen.has(id)) {
        seen.add(id);
        return;
      }
      let fresh = newPartyId(prefix);
      while (seen.has(fresh)) fresh = newPartyId(prefix);
      seen.add(fresh);
      question.value = fresh;
    });
  };
  visit("people", "person_id", "p");
  visit("entities", "entity_id", "e");
}

// --- the picker sync -------------------------------------------------------

/*
 * setChoices()
 * Puts `choices` on one picker. A pick that is still offered stays. A pick that
 * is not is cleared and returned as { value }, so the client can be told what
 * went. Returns null when nothing was cleared.
 *
 * Set a dynamic panel's template question BEFORE its rows: survey-core copies
 * the template's new choices onto every row.
 */
export function setChoices(question, choices) {
  if (!question) return null;
  const current = question.value;
  question.choices = choices;
  if (current === undefined || current === null || current === "") return null;

  if (choices.some((choice) => choice.value === current)) {
    // Re-assert it: assigning choices can drop a value that is still legal.
    question.value = current;
    return null;
  }

  // Leaving it looks harmless but is the worst case: survey-core keeps a value
  // that is not in its own choices, so a required picker passes validation and
  // the row points at a party that is gone. Clearing it makes it an ordinary
  // unanswered question.
  question.value = undefined;
  return { value: current };
}

// A picker counts as seen when it and every panel around it are visible. A
// hidden pick is still cleared, without a notice the client could not act on.
const isShown = (question) => question.isVisible && question.isParentVisible !== false;

/*
 * readParties()
 * The people, entities and role rows of the current answers, through the shared
 * parse, so a party is named here as the server names it. An unnamed party is
 * offered under its panel title ("Person 2", "Entity 1").
 */
function readParties(model) {
  const parsed = rules.parseSubmission(model.data || {});
  const label = (fallback) => (party, index) => ({
    value: party.id,
    text: rules.partyDisplayName(party) || `${fallback} ${index + 1}`,
  });
  const once = (choices) =>
    choices.filter(
      (choice, i) => choice.value && choices.findIndex((c) => c.value === choice.value) === i
    );
  return {
    people: once(parsed.people.map(label("Person"))),
    entities: once(parsed.entities.map(label("Entity"))),
    rows: parsed.rows,
  };
}

/*
 * syncPartyChoices()
 * Mints missing ids, then fills the three pickers:
 *
 *   holder_ref                people and entities, minus the row's own entity
 *   primary_entity_id         the entities the person holds a role in
 *   ceo_equivalent_person_id  every person
 *
 * primary_entity_id is filled after holder_ref, from the answers as they are
 * once the stale holder picks are cleared. Returns { cleared }, one item for
 * each shown pick it cleared: { question, value, text, where, stillExists },
 * where `where` names the panel the pick sits in.
 */
export function syncPartyChoices(model) {
  if (!model || syncing.has(model)) return { cleared: [] };
  syncing.add(model);
  try {
    ensurePartyIds(model);
    const before = readParties(model);
    const parties = [...before.people, ...before.entities];

    if (!lastLabels.has(model)) lastLabels.set(model, new Map());
    const labels = lastLabels.get(model);
    parties.forEach((choice) => labels.set(choice.value, choice.text));

    const cleared = [];
    const apply = (question, choices, where, existing) => {
      const gone = setChoices(question, choices);
      if (gone && isShown(question)) {
        cleared.push({
          question: question.name,
          value: gone.value,
          text: labels.get(gone.value) || "",
          where,
          stillExists: existing.some((choice) => choice.value === gone.value),
        });
      }
    };

    // 1. holder_ref, on each entity's role template and on every role row.
    const entities = model.getQuestionByName("entities");
    if (entities) {
      const holderPicker = (container) =>
        container?.getQuestionByName("role_holders")?.template?.getQuestionByName("holder_ref");
      // The entities template too, so a new entity's rows start with the list.
      setChoices(holderPicker(entities.template), parties);

      (entities.panels || []).forEach((panel) => {
        const ownId = panel.getQuestionByName("entity_id")?.value;
        const own = before.entities.find((choice) => choice.value === ownId);
        const choices = parties.filter((choice) => choice.value !== ownId);
        const roleHolders = panel.getQuestionByName("role_holders");
        if (!roleHolders) return;
        setChoices(roleHolders.template?.getQuestionByName("holder_ref"), choices);
        (roleHolders.panels || []).forEach((row) => {
          apply(row.getQuestionByName("holder_ref"), choices, own ? own.text : "", parties);
        });
      });
    }

    // 2. primary_entity_id: held entities only (R6).
    const people = model.getQuestionByName("people");
    if (people) {
      const { rows } = readParties(model);
      // A new person holds nothing yet.
      setChoices(people.template?.getQuestionByName("primary_entity_id"), []);
      (people.panels || []).forEach((panel) => {
        const id = panel.getQuestionByName("person_id")?.value;
        const held = before.entities.filter((entity) =>
          rows.some((row) => row.holderRef === id && row.entityId === entity.value)
        );
        const person = before.people.find((choice) => choice.value === id);
        apply(panel.getQuestionByName("primary_entity_id"), held, person ? person.text : "", before.entities);
      });
    }

    // 3. The CEO/SMO pick (KTD22).
    apply(model.getQuestionByName("ceo_equivalent_person_id"), before.people, "", before.people);

    return { cleared };
  } finally {
    syncing.delete(model);
  }
}

/*
 * watchPartyChoices()
 * Keeps the pickers in sync while the client types, adds a panel or removes
 * one. onCleared(items) gets the picks a sync cleared; onSynced() runs after
 * every sync, which is where the view saves its draft. Returns the unsubscribe.
 */
export function watchPartyChoices(model, { onCleared, onSynced } = {}) {
  const handler = () => {
    if (syncing.has(model)) return;
    const { cleared } = syncPartyChoices(model);
    if (cleared.length && onCleared) onCleared(cleared);
    if (onSynced) onSynced();
  };
  const events = [model.onValueChanged, model.onDynamicPanelAdded, model.onDynamicPanelRemoved];
  events.forEach((event) => event.add(handler));
  return () => events.forEach((event) => event.remove(handler));
}

/*
 * clearedSelectionMessages()
 * One sentence for each cleared pick, for the notice the view shows.
 */
export function clearedSelectionMessages(cleared) {
  return (cleared || []).map((item) => {
    const party = item.text ? `"${item.text}"` : "A person or entity";
    if (item.question === "holder_ref") {
      const where = item.where ? ` in ${item.where}` : "";
      return `${party} was removed, so a role${where} no longer has a person or entity. Select one again.`;
    }
    if (item.question === "primary_entity_id") {
      const who = item.where || "A person";
      return item.stillExists
        ? `${who} no longer holds a role in ${party}, so the entity they sit under was cleared. Choose it again.`
        : `${party} was removed, so the entity ${who} sits under was cleared. Choose it again.`;
    }
    return `${party} was removed, so the CEO or senior managing official was cleared. Select them again.`;
  });
}

// --- the draft --------------------------------------------------------------

/*
 * restoreDraft()
 * Loads a saved draft into the model and syncs the pickers, so the restored
 * picks are offered before the form renders. No panel event fires on a data
 * load, so nothing else would sync them.
 */
export function restoreDraft(model, draft) {
  if (isPlainObject(draft)) model.data = draft;
  return syncPartyChoices(model);
}

// The TFN test the server redacts by (isTfnLeafKey in helper/redactTfn.js).
const isTfnKey = (key, fieldNames) => /(^|_)tfn$/i.test(key) || fieldNames.includes(key);

/*
 * tfnQuestionNames()
 * The names of the form's TFN questions: flagged `tfn: true`, or named like a
 * TFN. Walks the schema as auditTfnQuestionsInForm (helper/clientOnboardingCore.js)
 * does, matrix columns and rows included.
 */
export function tfnQuestionNames(schema) {
  const names = [];
  const visit = (node) => {
    if (Array.isArray(node)) return node.forEach(visit);
    if (!isPlainObject(node)) return;
    const name = node.name || node.value;
    if (typeof name === "string" && (node.tfn === true || /(^|_)tfn$/i.test(name))) {
      if (!names.includes(name)) names.push(name);
    }
    ["pages", "elements", "templateElements", "columns", "rows"].forEach((key) => visit(node[key]));
  };
  visit(schema);
  return names;
}

/*
 * draftWithoutTfn()
 * A copy of the answers with every TFN answer removed, at any depth, for the
 * browser draft. A TFN is never kept in the browser (R16); the client enters it
 * again after a reload. The answers are not changed.
 */
export function draftWithoutTfn(data, fieldNames = []) {
  const strip = (node) => {
    if (Array.isArray(node)) return node.map(strip);
    if (!isPlainObject(node)) return node;
    const copy = {};
    for (const [key, value] of Object.entries(node)) {
      if (!isTfnKey(key, fieldNames)) copy[key] = strip(value);
    }
    return copy;
  };
  return isPlainObject(data) ? strip(data) : {};
}

// --- uploads -----------------------------------------------------------------

const UPLOAD_FAILED_MESSAGE = "The file could not be uploaded. Please try again.";

// The id of the person or entity panel a question sits in, at any depth, or
// null for a question outside the party lists. A dynamic panel's row has no
// parent, so the walk goes on from the paneldynamic question that holds it.
function partyIdOf(question) {
  for (let q = question; q; q = q.parentQuestion) {
    for (let panel = q.parent; panel; panel = panel.parent) {
      const id = (panel.getQuestionByName("person_id") || panel.getQuestionByName("entity_id"))?.value;
      if (typeof id === "string" && (ID_PATTERNS.p.test(id) || ID_PATTERNS.e.test(id))) return id;
    }
  }
  return null;
}

/*
 * uploadFieldName()
 * The folder an upload is stored in, under the response id: <party id>/<name>
 * for a question in a person or entity panel, else the bare question name, as
 * in v1. survey-core passes only the bare name, and the server stores each file
 * at <response id>/<field name>/<file name>, so without the party id two trusts'
 * "Trust Deed.pdf" would be one object, the second upload replacing the first.
 */
export function uploadFieldName(question) {
  const partyId = partyIdOf(question);
  return partyId ? `${partyId}/${question.name}` : question.name;
}

/*
 * createUploadHandler()
 * The onUploadFiles handler. It runs only for a file question with
 * storeDataAsText: false; any other keeps the file in the answer. check(options)
 * resolves { valid, message } (the proof-of-address check). Each file is posted
 * on its own, because the upload endpoint takes one file for each request, and
 * post(formData) resolves the endpoint's { name, url }.
 *
 * Each file keeps the storage path the server answers with as its `key`. The
 * staff screens open and zip a stored file only by its key: `content` is a
 * signed URL that expires after five minutes. survey-core keeps only name, type
 * and content from the callback, so the key goes on the answer after the
 * callback stores it, matched by the signed URL.
 */
export function createUploadHandler({ responseId, check, post }) {
  return async function onUploadFiles(sender, options) {
    const question = options.question;
    const fieldName = uploadFieldName(question);
    let stored;
    try {
      const poa = check ? await check(options) : { valid: true };
      if (!poa.valid) {
        options.callback("error", poa.message);
        return;
      }
      stored = await Promise.all(
        options.files.map(async (file) => {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("response_id", responseId);
          formData.append("field_name", fieldName);
          formData.append("file_name", file.name);
          const data = await post(formData);
          return { file, content: data.url, key: data.name };
        })
      );
    } catch {
      options.callback("error", UPLOAD_FAILED_MESSAGE);
      return;
    }

    options.callback("success", stored.map(({ file, content }) => ({ file, content })));
    const value = question.value;
    if (!Array.isArray(value)) return;
    question.value = value.map((item) => {
      const match = isPlainObject(item) && !item.key && stored.find((upload) => upload.content === item.content);
      return match ? { ...item, key: match.key } : item;
    });
  };
}

// --- the submission ----------------------------------------------------------

// An upload kept inline in the answer (storeDataAsText, the survey-core
// default and the seeded template's): its content is the file itself.
const isInlineUpload = (item) => typeof item.content === "string" && item.content.startsWith("data:");

// A file question's uploads, each with its storage key. A key the upload
// stored is kept: it names where the file is. A stored upload without one gets
// the key the upload handler would have stored it under. An inline upload gets
// none: no stored object exists for a key to name.
function keyedUploads(question, responseId) {
  const value = question.value;
  if (!Array.isArray(value)) return value;
  return value.map((item) =>
    isPlainObject(item) && typeof item.key !== "string" && !isInlineUpload(item)
      ? { ...item, key: `${responseId}/${uploadFieldName(question)}/${item.name}` }
      : item
  );
}

// A question's answer, with every upload in it keyed, at any depth: every
// upload of the v2 template sits in a person or entity panel.
function answerOf(question, responseId) {
  const type = question.getType();
  if (type === "file") return keyedUploads(question, responseId);
  if (type !== "paneldynamic") return question.value;

  const rows = question.value;
  if (!Array.isArray(rows)) return rows;
  return rows.map((row, index) => {
    const panel = question.panels[index];
    if (!isPlainObject(row) || !panel) return row;
    const copy = { ...row };
    panel.questions.forEach((inner) => {
      const innerType = inner.getType();
      if (inner.name in copy && (innerType === "file" || innerType === "paneldynamic")) {
        copy[inner.name] = answerOf(inner, responseId);
      }
    });
    return copy;
  });
}

/*
 * collectAnswers()
 * The answers to POST, built as the v1 view builds them: one key for each
 * question the data names. Every stored upload, in a party panel or not,
 * carries the key it is stored under; an inline one stays as it is
 * (keyedUploads).
 */
export function collectAnswers(model, responseId) {
  const answers = {};
  for (const key of Object.keys(model.data || {})) {
    const question = model.getQuestionByName(key);
    if (!question) continue;
    answers[key] = answerOf(question, responseId);
  }
  return answers;
}

/*
 * createSubmitGate()
 * The onCompleting handler. It always stops survey-core's own completion, runs
 * the shared rules on the answers (R10), and calls onRefused({ message, errors })
 * when they find errors, so the form stays open. Otherwise it calls
 * submit(answers), which POSTs and resolves true when the server accepted the
 * submission. Only then does it complete the survey with doComplete(). One POST
 * at a time: a second click while one is out does nothing.
 */
export function createSubmitGate({ collect, submit, onRefused }) {
  let completing = false;
  let inFlight = false;

  return function onCompleting(sender, options) {
    // The doComplete() below, after the server accepted the submission.
    if (completing) return;
    options.allow = false;
    options.allowComplete = false;
    if (inFlight) return;

    const answers = collect(sender);
    const { errors } = rules.checkSubmission(answers);
    if (errors.length) {
      onRefused({ message: RULES_REFUSED_MESSAGE, errors });
      return;
    }

    inFlight = true;
    Promise.resolve()
      .then(() => submit(answers))
      .then(
        (accepted) => {
          inFlight = false;
          if (!accepted) return;
          completing = true;
          sender.doComplete();
        },
        () => {
          inFlight = false;
          onRefused({ message: SUBMIT_FAILED_MESSAGE, errors: [] });
        }
      );
  };
}

/*
 * groupErrorsByParty()
 * The errors grouped under the party they name, in the order they came. Errors
 * about the whole form have partyName "". Each message shows once, with a count
 * when it repeats: every blank role row of one entity has the same text, and
 * one line would let the client fix one row and be refused again. The server's
 * errors carry no row, so the count is the only way to tell them apart.
 */
export function groupErrorsByParty(errors) {
  const groups = [];
  for (const error of Array.isArray(errors) ? errors : []) {
    if (!error || !error.message) continue;
    const partyName = error.partyName || "";
    let group = groups.find((g) => g.partyName === partyName);
    if (!group) {
      group = { partyName, counts: new Map() };
      groups.push(group);
    }
    group.counts.set(error.message, (group.counts.get(error.message) || 0) + 1);
  }
  return groups.map(({ partyName, counts }) => ({
    partyName,
    messages: Array.from(counts, ([message, count]) => (count > 1 ? `${message} (${count} times)` : message)),
  }));
}

/*
 * httpRedirectUrl()
 * The redirect, trimmed, when it is an absolute http(s) URL, else null. The view
 * assigns it to the location, so a javascript: or data: URL would run script on
 * the app's origin. The server checks it too (submitV2 and the settings save);
 * this is the last check before the sink.
 */
function httpRedirectUrl(value) {
  const url = typeof value === "string" ? value.trim() : "";
  if (!/^https?:\/\//i.test(url)) return null;
  try {
    return new URL(url).hostname ? url : null;
  } catch {
    return null;
  }
}

/*
 * submitOutcome()
 * What the view does with the POST's answer (the U6 contract):
 *
 *   2xx  accepted   { message, redirect }: redirect is an absolute http(s) URL
 *                   or null (httpRedirectUrl)
 *   400  refused    { message, errors }: the form stays open
 *   403  closed     the link closed while the client was filling it in
 *   else failed     { message }: the client can try again
 */
export function submitOutcome(status, body) {
  const data = isPlainObject(body) ? body : {};
  if (status >= 200 && status < 300) {
    return { kind: "accepted", message: data.message || "", redirect: httpRedirectUrl(data.redirect) };
  }
  if (status === 400) {
    return {
      kind: "refused",
      message: data.message || RULES_REFUSED_MESSAGE,
      errors: Array.isArray(data.errors) ? data.errors : [],
    };
  }
  if (status === 403) return { kind: "closed", message: LINK_CLOSED_MESSAGE };
  if (status === 404) return { kind: "failed", message: FORM_GONE_MESSAGE };
  if (status === 409 && data.message) return { kind: "failed", message: data.message };
  return { kind: "failed", message: SUBMIT_FAILED_MESSAGE };
}
